'use client'

import { cn } from '@/lib/utils'
import { FC, memo } from 'react'
import ReactMarkdown, { Components } from 'react-markdown'
import rehypeHighlight from 'rehype-highlight'
import remarkBreaks from 'remark-breaks'
import remarkGfm from 'remark-gfm'

export interface MarkdownViewProps {
  content: string
  className?: string
}

const components: Components = {
  // Links open in new tab
  a: ({ href, children, ...props }) => (
    <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
      {children}
    </a>
  ),
  // Images with max-width
  img: ({ src, alt, ...props }) => (
    // eslint-disable-next-line @next/next/no-img-element
    <img src={src} alt={alt || ''} loading="lazy" {...props} />
  ),
  // Checkbox for task lists
  input: ({ type, checked, ...props }) => {
    if (type === 'checkbox') {
      return <input type="checkbox" checked={checked} disabled {...props} />
    }
    return <input type={type} {...props} />
  },
}

// Markdown styles that match the existing VideoInfoPanel/SPDetailPanel design
const markdownStyles = `
  .markdown-view {
    font-size: 14px;
    line-height: 1.8;
    color: #444;
  }

  /* ダークモード（管理画面のOSダーク追従時）の視認性確保。
     公開/埋め込みはラッパー側で var(--foreground) に上書き済みのため影響しない。 */
  .dark .markdown-view,
  .dark .markdown-view h1,
  .dark .markdown-view h2,
  .dark .markdown-view h3,
  .dark .markdown-view h4,
  .dark .markdown-view h5,
  .dark .markdown-view h6,
  .dark .markdown-view strong {
    color: var(--foreground);
  }

  .markdown-view > *:first-child {
    margin-top: 0;
  }

  .markdown-view > *:last-child {
    margin-bottom: 0;
  }

  /* Headings */
  .markdown-view h1 {
    font-size: 1.3em;
    font-weight: bold;
    color: #333;
    margin: 1.5em 0 0.75em;
    padding-bottom: 0.3em;
    border-bottom: 1px solid #eee;
  }

  .markdown-view h2 {
    font-size: 1.2em;
    font-weight: bold;
    color: #333;
    margin: 1.25em 0 0.6em;
  }

  .markdown-view h3 {
    font-size: 1.05em;
    font-weight: bold;
    color: #333;
    margin: 1em 0 0.5em;
  }

  .markdown-view h4,
  .markdown-view h5,
  .markdown-view h6 {
    font-size: 1em;
    font-weight: bold;
    color: #444;
    margin: 0.75em 0 0.4em;
  }

  /* Paragraphs */
  .markdown-view p {
    margin: 0.75em 0;
  }

  /* Lists */
  .markdown-view ul,
  .markdown-view ol {
    margin: 0.75em 0;
    padding-left: 1.5em;
  }

  .markdown-view ul {
    list-style-type: disc;
  }

  .markdown-view ol {
    list-style-type: decimal;
  }

  .markdown-view li {
    margin: 0.25em 0;
  }

  .markdown-view li > ul,
  .markdown-view li > ol {
    margin: 0.25em 0;
  }

  /* Task lists */
  .markdown-view ul.contains-task-list {
    list-style-type: none;
    padding-left: 0;
  }

  .markdown-view li.task-list-item {
    display: flex;
    align-items: flex-start;
    gap: 0.5em;
  }

  .markdown-view li.task-list-item input[type="checkbox"] {
    margin-top: 0.35em;
    accent-color: #2196F3;
  }

  /* Links */
  .markdown-view a {
    color: #2196F3;
    text-decoration: none;
    transition: color 200ms ease;
  }

  .markdown-view a:hover {
    color: #1976D2;
    text-decoration: underline;
  }

  /* Strong and emphasis */
  .markdown-view strong {
    font-weight: bold;
    color: #333;
  }

  .markdown-view em {
    font-style: italic;
  }

  .markdown-view del {
    text-decoration: line-through;
    color: #888;
  }

  /* Inline code */
  .markdown-view code {
    font-family: 'SF Mono', Monaco, 'Andale Mono', monospace;
    font-size: 0.9em;
    background: #f5f5f5;
    padding: 0.15em 0.4em;
    border-radius: 4px;
    color: #e53935;
  }

  /* Code blocks */
  .markdown-view pre {
    margin: 1em 0;
    padding: 1em;
    background: #1e1e1e;
    border-radius: 8px;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }

  .markdown-view pre code {
    font-family: 'SF Mono', Monaco, 'Andale Mono', monospace;
    font-size: 0.85em;
    background: transparent;
    padding: 0;
    color: #d4d4d4;
    line-height: 1.5;
  }

  /* Syntax highlighting (highlight.js default dark theme) */
  .markdown-view .hljs-keyword,
  .markdown-view .hljs-selector-tag,
  .markdown-view .hljs-built_in {
    color: #569cd6;
  }

  .markdown-view .hljs-string,
  .markdown-view .hljs-attr {
    color: #ce9178;
  }

  .markdown-view .hljs-number,
  .markdown-view .hljs-literal {
    color: #b5cea8;
  }

  .markdown-view .hljs-comment {
    color: #6a9955;
  }

  .markdown-view .hljs-function,
  .markdown-view .hljs-title {
    color: #dcdcaa;
  }

  .markdown-view .hljs-variable,
  .markdown-view .hljs-params {
    color: #9cdcfe;
  }

  .markdown-view .hljs-type,
  .markdown-view .hljs-class {
    color: #4ec9b0;
  }

  /* Blockquotes */
  .markdown-view blockquote {
    margin: 1em 0;
    padding: 0.5em 0 0.5em 1em;
    border-left: 4px solid #2196F3;
    background: #f8f9fa;
    color: #666;
    font-style: italic;
  }

  .markdown-view blockquote p {
    margin: 0.5em 0;
  }

  /* Tables */
  .markdown-view table {
    width: 100%;
    margin: 1em 0;
    border-collapse: collapse;
    font-size: 0.9em;
  }

  .markdown-view th,
  .markdown-view td {
    padding: 0.6em 0.8em;
    border: 1px solid #ddd;
    text-align: left;
  }

  .markdown-view th {
    background: #f5f5f5;
    font-weight: bold;
    color: #333;
  }

  .markdown-view tr:nth-child(even) {
    background: #fafafa;
  }

  /* Images */
  .markdown-view img {
    max-width: 100%;
    height: auto;
    margin: 1em 0;
    border-radius: 8px;
  }

  /* Horizontal rules */
  .markdown-view hr {
    margin: 1.5em 0;
    border: none;
    border-top: 1px solid #eee;
  }
`

const MarkdownViewComponent: FC<MarkdownViewProps> = ({ content, className }) => {
  if (!content) {
    return null
  }

  return (
    <>
      <style>{markdownStyles}</style>
      <div className={cn('markdown-view', className)}>
        {/* remarkBreaks: 標準のMarkdownは単一改行を空白として扱うため、
            入力どおりに改行を反映させる（行末2スペース不要の <br> 化）。 */}
        <ReactMarkdown
          remarkPlugins={[remarkGfm, remarkBreaks]}
          rehypePlugins={[rehypeHighlight]}
          components={components}
        >
          {content}
        </ReactMarkdown>
      </div>
    </>
  )
}

export const MarkdownView = memo(MarkdownViewComponent)
